import * as IF from "infrontjs";
import template from "./form.html";
import { validateScope, resetValidation, bindLiveClear } from "../general/form-validation";
import { _lcs, locale, localizeCatalog } from "../../i18n";
import { renderTranslationSwitch, translatedField } from "../general/translation-switch";

// Translated fields, in the order the form above lists them. Labels are
// German source strings (dictionary keys), translated where they are read.
const TRANSLATED_FIELDS = [
    [ "title", "Titel" ],
    [ "subtitle", "Untertitel" ],
    [ "teaser", "Kurztext" ],
    [ "description", "Beschreibung" ]
];

// How many of an event's dates to list before summarising the rest.
const MAX_SHOWN_DATES = 12;

/**
 * <platform-event-form> — modal to edit one platform event (imported or
 * curated) from the saas-admin.
 *
 * Two things make this different from the host's own event form in the
 * client-admin:
 *
 * 1. DATES ARE READ-ONLY. The schedule belongs to the provider; a hand-edited
 *    date list would be replaced by the next run, so it is shown, not offered.
 *    An event that should disappear gets a status, not a deleted date.
 * 2. SAVING LOCKS. The API sets `isLocked` on any content edit, which takes the
 *    record out of the nightly import — otherwise every correction would be
 *    silently reverted overnight. The banner says so, and a locked event can be
 *    handed back to its source with one button.
 *
 * On a successful save it dispatches a bubbling "platform-event-saved" event.
 */
class PlatformEventForm extends HTMLElement
{
    constructor()
    {
        super();
        this._connected = false;
        this._id = null;
        this._categories = null;
    }

    connectedCallback()
    {
        if ( false === this._connected )
        {
            this.app = IF.App.get();
            this.render();
            this.bind();
            this._connected = true;
        }
    }

    render()
    {
        this.app.view.render( this, template, {} );

        this._modal = this.querySelector( '[data-id="evp-modal"]' );
        this._form = this.querySelector( '[data-id="evp-form-el"]' );
        this._title = this.querySelector( '[data-id="evp-title"]' );
        this._error = this.querySelector( '[data-id="evp-error"]' );
        this._submit = this.querySelector( '[data-id="evp-submit"]' );
        this._originText = this.querySelector( '[data-id="evp-origin-text"]' );
        this._lockNote = this.querySelector( '[data-id="evp-lock-note"]' );
        this._unlock = this.querySelector( '[data-id="evp-unlock"]' );
        this._duplicate = this.querySelector( '[data-id="evp-duplicate"]' );
        this._release = this.querySelector( '[data-id="evp-release"]' );
        this._images = this.querySelector( '[data-id="evp-images"]' );
        this._dates = this.querySelector( '[data-id="evp-dates"]' );
    }

    bind()
    {
        this.querySelector( '[data-id="evp-close"]' ).addEventListener( "click", () => this.close() );
        this.querySelector( '[data-id="evp-cancel"]' ).addEventListener( "click", () => this.close() );
        this.querySelector( '[data-id="evp-backdrop"]' ).addEventListener( "click", () => this.close() );
        this._form.addEventListener( "submit", ( e ) => this.onSubmit( e ) );
        this._unlock.addEventListener( "click", () => this.releaseToSource() );
        this._release.addEventListener( "click", () => this.releaseDuplicate() );

        bindLiveClear( this._form );

        this._onKeydown = ( e ) =>
        {
            if ( e.key === "Escape" && this._modal && !this._modal.hidden )
            {
                this.close();
            }
        };
        document.addEventListener( "keydown", this._onKeydown );
    }

    disconnectedCallback()
    {
        if ( this._onKeydown )
        {
            document.removeEventListener( "keydown", this._onKeydown );
        }
    }

    /* ------------------------------- Public API ------------------------------ */

    /**
     * @param {Object} event      Full event payload (GET /event/platform/:id).
     * @param {string} sourceName Display name of the feed it came from.
     */
    async openEdit( event, sourceName = "" )
    {
        this._id = event.id;
        this._event = event;

        this._clearError();
        this._form.reset();
        resetValidation( this._form );

        await this._ensureCategories();

        this._title.textContent = _lcs( "Veranstaltung bearbeiten" );
        this._renderOrigin( event, sourceName );

        this._val( "evp-title", event.title || "" );
        this._selectValue( "evp-category", event.category || "other" );
        this._selectValue( "evp-status", event.status || "published" );
        this._val( "evp-subtitle", event.subtitle || "" );
        this._val( "evp-teaser", event.teaser || "" );
        this._val( "evp-description", event.description || "" );
        this._renderTranslations( event );

        const location = event.location || {};
        this._val( "evp-locationname", location.name || "" );
        this._val( "evp-street", location.street || "" );
        this._val( "evp-postalcode", location.postalCode || "" );
        this._val( "evp-city", location.city || "" );

        this._val( "evp-ticketurl", event.ticketUrl || "" );
        this._val( "evp-websiteurl", event.websiteUrl || "" );
        this._field( "evp-featured" ).checked = event.isFeatured === true;
        this._field( "evp-outdoor" ).checked = event.isOutdoor === true;

        this._renderImages( Array.isArray( event.images ) ? event.images : [] );
        this._renderDates( Array.isArray( event.occurrences ) ? event.occurrences : [] );

        this._modal.hidden = false;
        this._field( "evp-title" ).focus();
    }

    close()
    {
        this._modal.hidden = true;
        this._clearError();
    }

    /* -------------------------------- Submit -------------------------------- */

    async onSubmit( e )
    {
        e.preventDefault();
        this._clearError();

        if ( !validateScope( this._form ) )
        {
            return;
        }

        const payload = {
            title       : this._val( "evp-title" ).trim(),
            subtitle    : this._val( "evp-subtitle" ).trim(),
            teaser      : this._val( "evp-teaser" ).trim(),
            description : this._val( "evp-description" ).trim(),
            category    : this._val( "evp-category" ),
            status      : this._val( "evp-status" ),

            location : {
                name       : this._val( "evp-locationname" ).trim(),
                street     : this._val( "evp-street" ).trim(),
                postalCode : this._val( "evp-postalcode" ).trim(),
                city       : this._val( "evp-city" ).trim(),
                // Coordinates are the provider's and are not editable here —
                // sending them back unchanged keeps the distance calculation
                // in the guest-app working.
                latitude   : this._event?.location?.latitude ?? null,
                longitude  : this._event?.location?.longitude ?? null
            },

            ticketUrl  : this._val( "evp-ticketurl" ).trim(),
            websiteUrl : this._val( "evp-websiteurl" ).trim(),
            isFeatured : this._field( "evp-featured" ).checked,
            isOutdoor  : this._field( "evp-outdoor" ).checked
        };

        await this._save(
            payload,
            _lcs( "„{title}“ wurde gespeichert und gegen den Import gesperrt.", { title: payload.title } )
        );
    }

    /**
     * Hand the event back to its feed: the next import overwrites it with the
     * provider's version again.
     */
    async releaseToSource()
    {
        const confirmed = await this.app.view.confirm(
            _lcs( "Wieder mit der Quelle synchronisieren?" ),
            _lcs( "Beim nächsten Import werden alle manuellen Änderungen an dieser Veranstaltung durch die Daten der Quelle ersetzt." ),
            _lcs( "Synchronisieren" ),
            _lcs( "Abbrechen" )
        );
        if ( !confirmed ) return;

        await this._save( { isLocked: false }, _lcs( "Die Veranstaltung folgt wieder ihrer Quelle." ) );
    }

    /**
     * Undo a wrong duplicate match: the event becomes visible again and is
     * locked, so the next import does not simply re-link it.
     */
    async releaseDuplicate()
    {
        const confirmed = await this.app.view.confirm(
            _lcs( "Keine Dublette?" ),
            _lcs( "Die Veranstaltung wird Gästen wieder angezeigt und beim nächsten Import nicht erneut automatisch zugeordnet." ),
            _lcs( "Wieder anzeigen" ),
            _lcs( "Abbrechen" )
        );
        if ( !confirmed ) return;

        await this._save( { duplicateOfEventId: null }, _lcs( "Die Veranstaltung wird wieder angezeigt." ) );
    }

    async _save( payload, successMessage )
    {
        this._setSubmitting( true );
        try
        {
            const { result } = await this.app.api.patch( "/event/platform/" + this._id, payload );
            if ( !result || +result.status !== 200 )
            {
                throw new Error( "Unexpected update response." );
            }

            this.app.view.toast( successMessage, "success" );
            this.close();
            this.dispatchEvent( new CustomEvent( "platform-event-saved", {
                bubbles: true,
                detail: { event: result.data || null }
            } ) );
        }
        catch ( err )
        {
            this._showError( this._errorForStatus( +err?.status ) );
        }
        finally
        {
            this._setSubmitting( false );
        }
    }

    /* ------------------------------- Rendering ------------------------------- */

    _renderOrigin( event, sourceName )
    {
        const origin = event.source === "import"
            ? _lcs( "Importiert aus „{source}“", {
                source : sourceName || event.sourceProvider || _lcs( "unbekannter Quelle" )
            } )
            : _lcs( "Redaktionell gepflegte Veranstaltung der Plattform" );

        const synced = event.sourceSyncedAt
            ? " · " + _lcs( "zuletzt abgeglichen {date}", { date: this._dateTimeText( event.sourceSyncedAt ) } )
            : "";
        this._originText.textContent = origin + synced;

        this._lockNote.textContent = event.isLocked
            ? _lcs( "Diese Veranstaltung wurde manuell bearbeitet und wird vom täglichen Import nicht mehr überschrieben." )
            : _lcs( "Beim Speichern wird die Veranstaltung gegen den täglichen Import gesperrt, damit deine Änderungen erhalten bleiben." );

        this._unlock.hidden = !event.isLocked || event.source !== "import";
        this._duplicate.hidden = !event.isDuplicate;
    }

    /**
     * The machine translations of the texts above, one language at a time.
     * Read-only: they are derived from the German fields and redone by the
     * API right after a save that changed them (core-api
     * ContentTranslationService.translateOnSave).
     */
    _renderTranslations( event )
    {
        const list = this.querySelector( '[data-id="evp-translation"]' );

        const show = ( lang ) =>
        {
            const rows = TRANSLATED_FIELDS
                .map( ( [ field, label ] ) => [ _lcs( label ), translatedField( event, lang, field ) ] )
                .filter( ( [ , value ] ) => value );

            list.innerHTML = rows.map( ( [ label, value ] ) =>
                "<dt>" + this._escape( label ) + "</dt><dd>" + this._escape( value ) + "</dd>" ).join( "" );
            list.hidden = rows.length === 0;
        };

        const languages = renderTranslationSwitch( this.querySelector( '[data-id="evp-langs"]' ), event, {
            "includeOriginal" : false,
            "onChange" : show
        } );

        if ( languages.length > 0 )
        {
            show( languages[ 0 ] );
        }
        else
        {
            list.innerHTML = "";
            list.hidden = true;
        }

    }

    /**
     * The event's pictures as the guest-app uses them: the first is the header
     * image on the card and the detail hero, the rest are the gallery.
     *
     * Read-only, like the dates — they belong to the feed, and the next import
     * would replace anything edited here. What IS worth showing an operator is
     * the credit line (a licence condition we have to carry) and whether the
     * file was copied into our own storage or is still being served from the
     * provider, because that is what a broken picture usually comes down to.
     */
    _renderImages( images )
    {
        if ( images.length === 0 )
        {
            this._images.innerHTML = '<p class="evp-form__dates-empty">' +
                this._escape( _lcs( "Diese Quelle liefert für diese Veranstaltung keine Bilder." ) ) + '</p>';
            return;
        }

        this._images.innerHTML = images.map( ( image, index ) =>
        {
            const isMirrored = !!image.sourceUrl && image.url !== image.sourceUrl;
            const meta = [
                image.credit || null,
                image.license || null,
                ( image.width && image.height ) ? image.width + "×" + image.height : null
            ].filter( Boolean ).join( " · " );

            return '<figure class="evp-image">' +
                '<a class="evp-image__link" href="' + this._escape( image.url ) + '" target="_blank" rel="noopener noreferrer">' +
                    '<img class="evp-image__img" src="' + this._escape( image.url ) + '" ' +
                         'alt="' + this._escape( image.alt || "" ) + '" loading="lazy" />' +
                    ( index === 0
                        ? '<span class="evp-image__badge"><i class="icon icon--star"></i> ' +
                            this._escape( _lcs( "Titelbild" ) ) + '</span>'
                        : "" ) +
                '</a>' +
                '<figcaption class="evp-image__meta">' +
                    '<span class="evp-image__store' + ( isMirrored ? "" : " evp-image__store--linked" ) + '">' +
                        '<i class="icon icon--' + ( isMirrored ? "shield-check" : "send-horizontal" ) + '"></i>' +
                        this._escape( isMirrored
                            ? _lcs( "in BELTURO gespeichert" )
                            : _lcs( "beim Anbieter verlinkt" ) ) +
                    '</span>' +
                    ( meta ? '<span class="evp-image__credit">' + this._escape( meta ) + '</span>' : "" ) +
                '</figcaption>' +
            '</figure>';
        } ).join( "" );
    }

    _renderDates( occurrences )
    {
        if ( occurrences.length === 0 )
        {
            this._dates.innerHTML = '<p class="evp-form__dates-empty">' +
                this._escape( _lcs( "Keine Termine hinterlegt." ) ) + '</p>';
            return;
        }

        const shown = occurrences.slice( 0, MAX_SHOWN_DATES );
        const rest = occurrences.length - shown.length;

        this._dates.innerHTML =
            shown.map( ( occurrence ) =>
                '<span class="evp-form__date' + ( occurrence.status === "cancelled" ? " evp-form__date--off" : "" ) + '">' +
                    this._escape( this._occurrenceText( occurrence ) ) +
                '</span>'
            ).join( "" ) +
            ( rest > 0
                ? '<span class="evp-form__date evp-form__date--more">' +
                    this._escape( _lcs( "+ {count} weitere", { count: rest } ) ) + '</span>'
                : "" );
    }

    _occurrenceText( occurrence )
    {
        const date = new Date( occurrence.startsAt );
        if ( isNaN( date.getTime() ) )
        {
            return "—";
        }

        // Weekday from Intl rather than from a dictionary entry: the platform
        // already knows the short name in every language we could ship.
        const day = date.toLocaleDateString( locale(), { weekday: "short" } ) + ", " +
            date.toLocaleDateString( locale(), { day: "2-digit", month: "2-digit", year: "2-digit" } );
        const time = occurrence.isAllDay
            ? _lcs( "ganztägig" )
            : date.toLocaleTimeString( locale(), { hour: "2-digit", minute: "2-digit" } );

        return day + " · " + time + ( occurrence.status === "cancelled" ? " " + _lcs( "(abgesagt)" ) : "" );
    }

    /* -------------------------------- Helpers -------------------------------- */

    // The category vocabulary is platform data — fetched once per session.
    async _ensureCategories()
    {
        if ( this._categories )
        {
            return;
        }

        try
        {
            const { result } = await this.app.api.get( "/event/catalog" );
            this._categories = ( result && +result.status === 200 && result.data && Array.isArray( result.data.categories ) )
                ? localizeCatalog( result.data.categories )
                : [];
        }
        catch ( e )
        {
            this._categories = [];
        }

        const select = this._field( "evp-category" );
        select.innerHTML = this._categories.map( ( category ) =>
            '<option value="' + this._escape( category.key ) + '">' + this._escape( category.title ) + '</option>'
        ).join( "" );
    }

    _selectValue( id, value )
    {
        const select = this._field( id );
        if ( value && !Array.from( select.options ).some( ( o ) => o.value === value ) )
        {
            const option = document.createElement( "option" );
            option.value = value;
            option.textContent = value;
            select.appendChild( option );
        }
        select.value = value || "";
    }

    _dateTimeText( value )
    {
        const date = new Date( value );
        if ( isNaN( date.getTime() ) )
        {
            return "–";
        }
        // "Uhr" is German punctuation for a time, not part of the format — the
        // dictionary drops it for languages that don't say it.
        return _lcs( "{time} Uhr", {
            time : date.toLocaleString( locale(), {
                day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
            } )
        } );
    }

    _field( id )
    {
        return this.querySelector( "#" + id );
    }

    _val( id, value )
    {
        const el = this._field( id );
        if ( value !== undefined )
        {
            if ( el ) el.value = value;
            return value;
        }
        return el ? el.value : "";
    }

    _escape( str )
    {
        return String( str === null || str === undefined ? "" : str ).replace( /[&<>"']/g, ( c ) =>
            ( { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ c ] ) );
    }

    _errorForStatus( status )
    {
        switch ( status )
        {
            case 400: return _lcs( "Bitte überprüfe deine Eingaben." );
            case 401:
            case 403: return _lcs( "Keine Berechtigung für diese Aktion." );
            case 404: return _lcs( "Die Veranstaltung wurde nicht gefunden." );
            default:  return _lcs( "Speichern fehlgeschlagen. Bitte versuche es später erneut." );
        }
    }

    _setSubmitting( on )
    {
        this._submit.disabled = on;
        this._submit.setAttribute( "aria-busy", String( on ) );
    }

    _showError( message )
    {
        this._error.textContent = message;
        this._error.hidden = false;
    }

    _clearError()
    {
        this._error.textContent = "";
        this._error.hidden = true;
    }
}

customElements.define( "platform-event-form", PlatformEventForm );
